import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Link,
  Outlet,
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import {
  AlertTriangle,
  CheckCircle2,
  CheckSquare,
  ChevronDown,
  ChevronUp,
  Loader2,
  Sparkles,
  XCircle,
} from "lucide-react";
import { useState } from "react";

import { WorkspaceShell } from "@/components/dravian/workspace-shell";
import {
  decisions as decisionsApi,
  type ApiConfidenceLabel,
  type ApiDecision,
  type ApiDecisionStatus,
} from "@/lib/api";

export const Route = createFileRoute("/$workspace/decisions")({
  head: () => ({ meta: [{ title: "Decisions — Dravian Horizon" }] }),
  component: DecisionsLayout,
});

function DecisionsLayout() {
  return <Outlet />;
}

export function DecisionsPage() {
  const { workspace: id } = useParams({ from: "/$workspace/decisions" });
  const q = useQuery({
    queryKey: ["decisions", id],
    queryFn: () => decisionsApi.list(id),
  });
  const decisions = q.data ?? [];

  return (
    <WorkspaceShell>
      <div className="p-5 max-w-[1200px] mx-auto">
        <div className="label-eyebrow mb-1">Decide</div>
        <h2 className="text-[20px] font-semibold mb-5">Decision Log</h2>

        <CreateDecisionCard workspaceId={id} />

        {q.isLoading ? (
          <div className="panel p-6 text-center text-[12px] text-muted-foreground">
            Loading decisions…
          </div>
        ) : decisions.length === 0 ? (
          <div className="panel p-8 text-center">
            <CheckSquare className="h-6 w-6 text-muted-foreground mx-auto mb-2" />
            <div className="text-[14px] font-semibold mb-1">No decisions yet</div>
            <p className="text-[12px] text-muted-foreground max-w-md mx-auto">
              Write down a decision you're considering and Horizon will evaluate
              it against this workspace's entities, signals, and scenarios.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {decisions.map((d) => (
              <DecisionCard key={d.id} decision={d} workspaceId={id} />
            ))}
          </div>
        )}
      </div>
    </WorkspaceShell>
  );
}

// ---------- Inline create ----------

function CreateDecisionCard({ workspaceId }: { workspaceId: string }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [input, setInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();
  const nav = useNavigate();

  const create = useMutation({
    mutationFn: () =>
      decisionsApi.create(workspaceId, {
        title: title.trim(),
        decision_input: input.trim(),
      }),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["decisions", workspaceId] });
      setTitle("");
      setInput("");
      setOpen(false);
      nav({
        to: "/$workspace/decisions/$decisionId",
        params: { workspace: workspaceId, decisionId: created.id },
      });
    },
    onError: (e) => setError(e instanceof Error ? e.message : "Failed to create"),
  });

  if (!open) {
    return (
      <div className="panel p-4 mb-6 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[13.5px] font-semibold mb-0.5">
            Evaluate a decision
          </div>
          <p className="text-[12px] text-muted-foreground">
            Describe a decision you're considering — Horizon evaluates it against
            the workspace context and returns a recommendation you can accept or
            reject.
          </p>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-[12px] font-semibold hover:bg-primary/90"
        >
          <Sparkles className="h-3.5 w-3.5" /> New decision
        </button>
      </div>
    );
  }

  const disabled =
    create.isPending || title.trim().length === 0 || input.trim().length === 0;
  return (
    <div className="panel p-5 mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[13.5px] font-semibold">New decision</div>
        <button
          onClick={() => {
            setOpen(false);
            setError(null);
          }}
          className="text-[11px] text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
      </div>
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
        Title
      </label>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Short label — e.g. Close corridor X for 72h"
        className="w-full mb-3 rounded-md border border-border bg-surface px-3 py-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-primary"
        maxLength={200}
      />
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
        Decision you're considering
      </label>
      <textarea
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder="Describe the decision in your own words. What are you thinking of doing, and roughly why?"
        rows={5}
        className="w-full mb-3 rounded-md border border-border bg-surface px-3 py-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-primary"
      />
      {error && (
        <div className="mb-3 text-[12px] text-risk-high">{error}</div>
      )}
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={() => create.mutate()}
          disabled={disabled}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-[12px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90"
        >
          {create.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Save as draft
        </button>
      </div>
    </div>
  );
}

// ---------- List card ----------

const STATUS_COPY: Record<ApiDecisionStatus, { label: string; classes: string }> = {
  draft: {
    label: "Draft",
    classes: "bg-muted/40 text-muted-foreground border border-border",
  },
  analyzed: {
    label: "Analyzed",
    classes: "bg-primary/15 text-primary border border-primary/30",
  },
  accepted: {
    label: "Accepted",
    classes: "bg-risk-low/15 text-risk-low border border-risk-low/30",
  },
  rejected: {
    label: "Rejected",
    classes: "bg-risk-high/15 text-risk-high border border-risk-high/30",
  },
  superseded: {
    label: "Superseded",
    classes: "bg-muted/40 text-muted-foreground border border-border",
  },
};

export function StatusBadge({ status }: { status: ApiDecisionStatus }) {
  const s = STATUS_COPY[status];
  return (
    <span
      className={`inline-flex items-center rounded px-2 py-0.5 text-[10px] font-semibold tracking-[0.12em] uppercase ${s.classes}`}
    >
      {s.label}
    </span>
  );
}

// Confidence has different semantics from risk: HIGH confidence is a positive
// signal (well-supported), LOW confidence is a caution. That is the opposite
// of what a risk badge communicates, so we deliberately DO NOT reuse RiskPill.
// The colors reinforce "level of support / evidence" rather than "alarm level".
const CONFIDENCE_STYLES: Record<ApiConfidenceLabel, string> = {
  high: "bg-risk-low/15 text-risk-low border border-risk-low/30",
  medium: "bg-risk-medium/15 text-risk-medium border border-risk-medium/30",
  low: "bg-muted/40 text-muted-foreground border border-border",
};

const CONFIDENCE_COPY: Record<ApiConfidenceLabel, string> = {
  high: "Well-supported",
  medium: "Some support",
  low: "Thin support",
};

export function ConfidenceBadge({
  label,
  showQualifier = false,
}: {
  label: ApiConfidenceLabel | null;
  showQualifier?: boolean;
}) {
  if (!label) {
    return (
      <span className="inline-flex items-center rounded px-2 py-0.5 text-[10px] font-semibold tracking-[0.12em] uppercase bg-muted/40 text-muted-foreground border border-border">
        No confidence
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-[10px] font-semibold tracking-[0.12em] uppercase ${CONFIDENCE_STYLES[label]}`}
      title={CONFIDENCE_COPY[label]}
    >
      {label} support
      {showQualifier && (
        <span className="hidden sm:inline text-[9.5px] font-normal opacity-80 normal-case tracking-normal">
          · {CONFIDENCE_COPY[label]}
        </span>
      )}
    </span>
  );
}

function DecisionCard({
  decision,
  workspaceId,
}: {
  decision: ApiDecision;
  workspaceId: string;
}) {
  const stale = isStale(decision);
  const updated = new Date(decision.updated_at);
  return (
    <Link
      to="/$workspace/decisions/$decisionId"
      params={{ workspace: workspaceId, decisionId: decision.id }}
      className="panel p-4 flex flex-col hover:border-primary/40 transition"
    >
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <StatusBadge status={decision.status} />
        <ConfidenceBadge label={decision.confidence_label} />
        {stale && (
          <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-semibold tracking-[0.12em] uppercase bg-risk-medium/15 text-risk-medium border border-risk-medium/30">
            <AlertTriangle className="h-3 w-3" /> Outdated
          </span>
        )}
        <span className="ml-auto text-[10px] text-muted-foreground font-mono">
          {relative(updated)}
        </span>
      </div>
      <h3 className="text-[14.5px] font-semibold leading-snug mb-1.5 line-clamp-2">
        {decision.title}
      </h3>
      <p className="text-[12px] text-muted-foreground line-clamp-3">
        {decision.summary || decision.decision_input}
      </p>
      {decision.recommendation && (
        <div className="mt-3 pt-3 border-t border-border flex items-center gap-1.5 text-[11px]">
          <RecommendationIcon rec={decision.recommendation} />
          <span className="text-muted-foreground uppercase tracking-wider font-semibold">
            {decision.recommendation.replace("_", " ")}
          </span>
        </div>
      )}
    </Link>
  );
}

function RecommendationIcon({
  rec,
}: {
  rec: NonNullable<ApiDecision["recommendation"]>;
}) {
  if (rec === "accept")
    return <CheckCircle2 className="h-3.5 w-3.5 text-risk-low" />;
  if (rec === "reject") return <XCircle className="h-3.5 w-3.5 text-risk-high" />;
  if (rec === "revise")
    return <ChevronUp className="h-3.5 w-3.5 text-risk-medium" />;
  return <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />;
}

// ---------- Helpers ----------

export function isStale(d: ApiDecision): boolean {
  // A decision is "stale" when the operator edited the input after the last
  // successful analyze — the recommendation shown is no longer trustworthy.
  if (!d.analyzed_at) return false;
  const analyzed = new Date(d.analyzed_at).getTime();
  const updated = new Date(d.updated_at).getTime();
  // Small skew tolerance — analyze itself bumps updated_at.
  return updated > analyzed + 1500 && d.status === "draft";
}

export function relative(d: Date): string {
  const ms = Date.now() - d.getTime();
  if (ms < 60_000) return "just now";
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.round(days / 30)}mo ago`;
}
